/**
 * Every number that is a game rule lives here and is read, never duplicated.
 *
 * These are the placeholder values agreed in `docs/open-questions.md` (Q2.4:
 * tune with data, not feel). `scripts/tune.ts` (M2) prints reachability
 * statistics so they can be adjusted from evidence during M7.
 *
 * `docs/rules.md` quotes these numbers; if you change one, change it there too.
 */

/** Terrain a tile can be. Relics sit *on* terrain and do not change its cost. */
export type Terrain = 'water' | 'sand' | 'grass' | 'forest' | 'mountain' | 'ruin'

/** Stamina to enter a tile of this terrain. `null` means impassable. */
export const MOVEMENT_COST: Readonly<Record<Terrain, number | null>> = {
  water: null,
  sand: 1,
  grass: 1,
  forest: 2,
  mountain: 3,
  ruin: 1,
} as const

/** Reputation tiers, low to high. Only the tier is ever shown to a player. */
export const REPUTATION_TIERS = ['doubted', 'unproven', 'known', 'trusted'] as const
export type ReputationTier = (typeof REPUTATION_TIERS)[number]

export const GAME_CONFIG = {
  /** Island grid. 20 x 12 = 240 tiles, one bit per tile in `seen_bits`. */
  grid: { width: 20, height: 12 },

  /** Stamina budgets: the rising day, then the sinking day's return trip. */
  stamina: { live: 30, return: 10 },

  /** Sight radius, in Chebyshev distance, from the tile you stand on. */
  sight: { base: 1, fromMountain: 2 },

  /** Generator guarantees, checked by property tests in M2. */
  generator: {
    ruins: { min: 2, max: 4 },
    relics: { min: 3, max: 6 },
    shores: { min: 3 },
    /** Minimum Manhattan distance between any two landing shores. */
    shoreSeparation: 8,
  },

  /** Lore fragment gating by how many the player already holds (Q6.3). */
  lore: {
    totalFragments: 60,
    tierUnlocks: [
      { foundAtMost: 15, tiers: [1] },
      { foundAtMost: 35, tiers: [1, 2] },
      { foundAtMost: Number.POSITIVE_INFINITY, tiers: [1, 2, 3] },
    ],
  },

  /** Reputation maths (Q5.1); tier boundaries from Q11.2. */
  reputation: {
    min: -1,
    max: 1,
    start: 0,
    onTrustworthy: 0.05,
    onFalse: -0.1,
    /** Multiplied into the score once per night, pulling it toward 0. */
    nightlyDecay: 0.98,
    /** Lower bound of each tier above `doubted`; read with `>=`. */
    tierThresholds: { unproven: -0.15, known: 0.15, trusted: 0.45 },
  },

  /**
   * Code names (Q4.5). The mood a name is drawn from is weighted by the
   * sender's reputation tier, but never below the floor, so every mood is
   * reachable from every tier and a name cannot be inverted into a score.
   * `content/<locale>/code-names.json` declares weights at or above this and
   * the content test checks it; the selector enforces it regardless.
   */
  codeName: { moodFloor: 0.05 },

  /** Letters (Q10.1, Q4.6). No free text anywhere. */
  letter: {
    statementsPerLetter: { min: 1, max: 3 },
    sendsPerDay: 1,
    draftsPerPlayer: 1,
    slice: { maxWidth: 8, maxHeight: 6 },
  },

  /** Recipient selection in the delivery job (Q4.1, Q4.2). */
  delivery: {
    /** A player is eligible if they were active within this many days. */
    activeWithinDays: 7,
    /** Below this pool size the "not the same recipient twice" rule is relaxed. */
    repeatRecipientPoolFloor: 3,
  },

  /** Guild-wide chart completion reward (Q5.4). */
  guild: { completionThreshold: 0.9 },

  /** How many days an island is playable before it finalises (Q1.1). */
  island: { livesForDays: 2 },

  /**
   * The username, which is the only string a player writes anywhere (Q11.5).
   * Here rather than in the server so registration and the register form
   * constrain it identically. The slur denylist lives with the server.
   */
  account: {
    username: {
      minLength: 3,
      maxLength: 20,
      /** A letter first, then letters, digits, underscore or hyphen. */
      pattern: /^[A-Za-z][A-Za-z0-9_-]{2,19}$/,
      /** Names that impersonate the game's own voice. */
      reservedNames: ['guild', 'keeper', 'admin'],
    },
  },
} as const
